import { isAudioFile, MAX_FILE_SIZE_BYTES } from '@/lib/utils/audio-file';
import { useConvexAction } from '@convex-dev/react-query';
import { useMutation } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from '~/convex/_generated/api';
import type { Id } from '~/convex/_generated/dataModel';

interface UseAudioUploadOptions {
  templateId?: Id<'templates'> | null;
  onUploadStart?: () => void;
  onUploadComplete: (reportId: Id<'reports'>) => void;
  onError: (message: string) => void;
}

/**
 * Shared hook for uploading audio files to R2 and creating reports.
 * Used by AudioFilePicker and AudioDropZone.
 */
export function useAudioUpload({
  templateId,
  onUploadStart,
  onUploadComplete,
  onError,
}: UseAudioUploadOptions) {
  const { mutateAsync: getUploadUrl } = useMutation({
    mutationFn: useConvexAction(api.uploadActions.getUploadUrl),
  });

  const { mutateAsync: createReportFromUpload } = useMutation({
    mutationFn: useConvexAction(api.uploadActions.createReportFromUpload),
  });

  const uploadFile = useCallback(
    async (file: File) => {
      if (!isAudioFile(file)) {
        onError('Please select an audio file (MP3, WAV, M4A, etc.)');
        return;
      }

      if (file.size > MAX_FILE_SIZE_BYTES) {
        onError('File must be under 100 MB');
        return;
      }

      onUploadStart?.();

      try {
        const { uploadUrl, r2Key } = await getUploadUrl({
          filename: file.name,
          contentType: file.type || 'application/octet-stream',
          fileSize: file.size,
        });

        const uploadResponse = await fetch(uploadUrl, {
          method: 'PUT',
          body: file,
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(`Upload failed: ${uploadResponse.statusText}`);
        }

        const reportId = await createReportFromUpload({
          r2Key,
          ...(templateId ? { templateId } : {}),
        });

        onUploadComplete(reportId);
      } catch (error) {
        console.error('Upload error:', error);
        onError(
          error instanceof Error
            ? error.message
            : 'Upload failed. Please try again.',
        );
      }
    },
    [
      templateId,
      getUploadUrl,
      createReportFromUpload,
      onUploadStart,
      onUploadComplete,
      onError,
    ],
  );

  return { uploadFile };
}
